<template>
  <v-dialog
    v-model="dialogVisible"
    max-width="1200px"
    scrollable
    persistent
    @keydown.esc="close"
  >
    <v-card>
      <v-card-title class="d-flex align-center">
        <div>
          {{ title }}
          <small class="text-medium-emphasis ml-2">
            ({{ totalMessages }} {{ itemsLabel }})
          </small>
        </div>
        <v-spacer />
        <v-btn-toggle
          v-if="hasTranslations"
          v-model="showOriginal"
          density="compact"
          variant="outlined"
          divided
          mandatory
          class="mr-3"
        >
          <v-btn :value="false" size="small">Перевод</v-btn>
          <v-btn :value="true" size="small">Оригинал</v-btn>
        </v-btn-toggle>
        <v-btn icon="mdi-close" variant="text" @click="close" />
      </v-card-title>

      <v-card-text class="pa-0" style="height: 70vh;">
        <div
          class="messages-container"
          ref="container"
          @scroll="onScroll"
        >
          <div v-if="loading && items.length === 0" class="text-center pa-4">
            <v-progress-circular indeterminate color="primary" />
            <div class="mt-2">Загрузка...</div>
          </div>

          <div
            v-if="items.length === 0 && !loading"
            class="text-center pa-4 text-medium-emphasis"
          >
            {{ emptyLabel }}
          </div>

          <div
            v-for="(msg, idx) in items"
            :key="msg.content_id || idx"
            class="message-item"
          >
            <v-row>
              <v-col cols="12" md="3">
                <div v-if="siteName(msg)" class="message-site">{{ siteName(msg) }}</div>
                <div class="message-date">
                  {{ formatDate(msg.published_at || msg.collected_at) }}
                </div>
                <div class="message-date-note">
                  {{ msg.published_at ? 'опубликовано' : 'собрано' }}
                </div>
                <div v-if="msg.content_url || msg.source_url" class="message-link">
                  <a :href="msg.content_url || msg.source_url" target="_blank" class="text-primary">
                    Оригинал
                    <v-icon size="small" class="ml-1">mdi-open-in-new</v-icon>
                  </a>
                  <v-tooltip v-if="isOnionMsg(msg)" text="Адрес в сети Tor: откроется только в Tor Browser (или Brave в режиме Tor). Клик копирует ссылку." location="top">
                    <template #activator="{ props }">
                      <v-chip v-bind="props" size="x-small" color="deep-purple" variant="tonal" class="ml-1" @click.prevent="copyOnionUrl(msg)">
                        <v-icon start size="x-small">mdi-incognito</v-icon>Tor
                      </v-chip>
                    </template>
                  </v-tooltip>
                </div>
                <div v-if="extractionBadge(msg)" class="mt-1">
                  <v-tooltip :text="extractionBadge(msg).tooltip" location="top">
                    <template #activator="{ props }">
                      <v-chip v-bind="props" size="x-small" :color="extractionBadge(msg).color" variant="tonal">
                        <v-icon start size="x-small">{{ extractionBadge(msg).icon }}</v-icon>
                        {{ extractionBadge(msg).label }}
                      </v-chip>
                    </template>
                  </v-tooltip>
                </div>
                <div v-if="canRetry(msg)" class="mt-1">
                  <v-btn
                    size="x-small" variant="tonal" color="primary"
                    :loading="msg._retrying" :disabled="msg._retried"
                    @click="retryCrawl(msg)"
                  >
                    <v-icon start size="x-small">mdi-refresh</v-icon>
                    {{ msg._retried ? 'В очереди' : 'Повторить' }}
                  </v-btn>
                </div>
              </v-col>
              <v-col cols="12" md="9">
                <div class="message-content">
                  <!-- Author header (X.com only) -->
                  <div v-if="isTwitter(msg) && authorHandle(msg)" class="author-header mb-2">
                    <v-avatar size="32" class="mr-2">
                      <v-img v-if="authorAvatar(msg)" :src="authorAvatar(msg)" cover></v-img>
                      <v-icon v-else color="grey-lighten-1">mdi-account</v-icon>
                    </v-avatar>
                    <div class="d-flex flex-column">
                      <span class="author-name">{{ authorName(msg) }}</span>
                      <a
                        :href="`https://x.com/${authorHandle(msg)}`"
                        target="_blank"
                        rel="noopener"
                        class="author-handle"
                      >@{{ authorHandle(msg) }}</a>
                    </div>
                  </div>
                  <div v-if="msgTitle(msg)" class="article-title">
                    {{ msgTitle(msg) }}
                  </div>
                  <div v-if="msg.original_lang" class="text-caption text-medium-emphasis mb-1">
                    <v-icon size="x-small">mdi-translate</v-icon>
                    {{ showOriginal ? ('Оригинал (' + msg.original_lang + ')') : 'Перевод с ' + msg.original_lang }}
                  </div>
                  <div
                    class="main-text"
                    style="white-space: pre-wrap; word-wrap: break-word;"
                    v-html="renderContent(msg)"
                  ></div>
                  <div
                    v-if="msgContent(msg) && msgContent(msg).length > 300"
                    class="mt-1"
                  >
                    <a
                      href="#"
                      class="text-primary text-decoration-none"
                      @click.prevent="msg._expanded = !msg._expanded"
                    >
                      {{ msg._expanded ? 'Скрыть' : 'Показать' }}
                    </a>
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>

          <div
            v-if="loading && items.length > 0"
            class="text-center pa-3"
          >
            <v-progress-circular indeterminate color="primary" size="small" />
          </div>

          <div
            v-if="!hasMore && items.length > 0"
            class="text-center pa-3 text-medium-emphasis"
          >
            <small>Всё загружено</small>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { dropLeftoverMarkers } from '@/utils/messageMarkers';
import apiDataClient from '@/apiDataClient';
import apiClient from '@/apiClient';
import { decodeHostname } from '@/utils/punycode';

export default {
  name: 'SourceContentModal',
  props: {
    modelValue: { type: Boolean, default: false },
    sourceId: { type: [Number, String], default: null },
    title: { type: String, default: '' },
    itemsLabel: { type: String, default: 'сообщений' },
    emptyLabel: { type: String, default: 'Сообщения не найдены' },
    // When the underlying source is a search query, pass the query text
    // here to highlight its occurrences in each message (case-insensitive
    // substring match).
    highlightTerm: { type: String, default: '' },
    // Граф связей каналов: показать только посты, где упомянут канал `mention`
    // (edgeType: forward | link | reply | '' = любой).
    mention: { type: String, default: '' },
    edgeType: { type: String, default: '' },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      items: [],
      totalMessages: 0,
      loading: false,
      hasMore: true,
      offset: 0,
      limit: 20,
      showOriginal: false,   // Перевод (false) / Оригинал (true)
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.modelValue;
      },
      set(v) {
        this.$emit('update:modelValue', v);
      },
    },
    hasTranslations() {
      return this.items.some(i => i.content_original || i.title_original);
    },
  },
  watch: {
    modelValue(v) {
      if (v) {
        this.reset();
        this.loadPage();
      }
    },
  },
  methods: {
    // Onion-контент (Tor): флаг platform_data.is_onion или .onion в адресе.
    isOnionMsg(msg) {
      const u = (msg && (msg.content_url || msg.source_url)) || '';
      return !!(msg && msg.platform_data && msg.platform_data.is_onion) || /\.onion(\/|$)/i.test(u);
    },
    async copyOnionUrl(msg) {
      const u = (msg && (msg.content_url || msg.source_url)) || '';
      try { await navigator.clipboard.writeText(u); } catch (e) { /* clipboard недоступен — ссылка остаётся кликабельной */ }
    },
    reset() {
      this.items = [];
      this.offset = 0;
      this.hasMore = true;
      this.totalMessages = 0;
      this.showOriginal = false;
    },
    async loadPage() {
      if (this.loading || !this.hasMore || !this.sourceId) return;
      this.loading = true;
      try {
        const { data } = await apiDataClient.get('/source-content/by-source', {
          params: {
            source_id: this.sourceId,
            offset: this.offset,
            limit: this.limit,
            ...(this.mention ? { mention: this.mention } : {}),
            ...(this.mention && this.edgeType ? { edge_type: this.edgeType } : {}),
          },
        });
        const next = (data.items || []).map((x) => ({ ...x, _expanded: false }));
        if (this.offset === 0) {
          this.items = next;
          this.totalMessages = data.total || 0;
        } else {
          this.items.push(...next);
        }
        this.offset += this.limit;
        this.hasMore =
          next.length === this.limit && this.items.length < (data.total || 0);
      } catch (e) {
        console.error('SourceContentModal load error:', e);
        this.hasMore = false;
      } finally {
        this.loading = false;
      }
    },
    onScroll() {
      const c = this.$refs.container;
      if (!c) return;
      const position = c.scrollTop + c.clientHeight;
      const threshold = 100;
      if (
        position >= c.scrollHeight - threshold
        && this.hasMore
        && !this.loading
      ) {
        this.loadPage();
      }
    },
    formatDate(iso) {
      if (!iso) return '';
      try {
        const d = new Date(iso);
        if (isNaN(d.getTime()) || d.getFullYear() === 1970) return '';
        const pad = (n) => String(n).padStart(2, '0');
        return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
      } catch {
        return iso;
      }
    },
    truncate(text, n) {
      if (!text) return '';
      return text.length > n ? text.substring(0, n) + '…' : text;
    },
    isTwitter(msg) {
      return (msg?.platform_name || '').toLowerCase() === 'twitter';
    },
    authorHandle(msg) {
      // Dzen: prefer platform_data.publisher_url (channel) over source_url
      // (which is the article URL like dzen.ru/a/{id}).
      const pub = msg?.platform_data?.publisher_url || '';
      if (pub) {
        const m = pub.match(/^https?:\/\/(?:www\.)?dzen\.ru\/([^/?#]+)(?:\/|$)/i);
        if (m && m[1] !== 'a') return m[1];
      }
      const direct = (msg?.platform_data?.author_username || '').replace(/^@/, '');
      if (direct) return direct;
      // Fallback: parse handle out of source_url for old / partial docs.
      const url = msg?.source_url || '';
      let m = url.match(/^https?:\/\/(?:www\.)?(?:x|twitter)\.com\/([^/?#]+)(?:\/|$)/i);
      if (m) return m[1];
      m = url.match(/^https?:\/\/(?:www\.)?t\.me\/([^/?#]+)(?:\/|$)/i);
      if (m && m[1] !== 's') return m[1];
      m = url.match(/^https?:\/\/(?:www\.)?dzen\.ru\/([^/?#]+)(?:\/|$)/i);
      if (m && m[1] !== 'a') return m[1];
      return '';
    },
    authorName(msg) {
      return msg?.platform_data?.author_name || this.authorHandle(msg);
    },
    authorAvatar(msg) {
      return msg?.platform_data?.author_profile_image_url || '';
    },
    escapeHtml(s) {
      return (s || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },
    escapeRegExp(s) {
      return (s || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },
    msgContent(msg) {
      // Show original (non-Russian) text when the toggle is on and it exists.
      return (this.showOriginal && msg.content_original) ? msg.content_original : (msg.content || '');
    },
    msgTitle(msg) {
      return (this.showOriginal && msg.title_original) ? msg.title_original : (msg.title || '');
    },
    // Site/domain of the article (shown left) — for search/web docs.
    siteName(msg) {
      // Для Telegram все посты модалки из одного канала — «t.me» в каждой строке бесполезен
      if ((msg.platform_name || '').toLowerCase() === 'telegram') return '';
      const url = msg.content_url || msg.source_url || '';
      try {
        // hostname всегда приходит в punycode, поэтому кириллические домены
        // показываем в исходном виде: «сайт.рф», а не «xn--80aswg.xn--p1ai»
        return decodeHostname(new URL(url).hostname).replace(/^www\./, '');
      } catch (e) {
        return '';
      }
    },
    // Retry available for snippet-only docs of a search-engine source.
    canRetry(msg) {
      return !!this.sourceId
        && msg.extraction_method === 'snippet'
        && !!(msg.content_url || msg.source_url);
    },
    async retryCrawl(msg) {
      const url = msg.content_url || msg.source_url;
      if (!url) return;
      msg._retrying = true;
      try {
        // В окне группы поисковиков sourceId — список; перекачка идёт по источнику самого документа
        await apiClient.post(`/user-sources/by-source/${msg.source_id || this.sourceId}/recrawl`, { url });
        msg._retried = true;  // queued; result appears on next reopen if upgraded
      } catch (e) {
        msg._retrying = false;
      } finally {
        msg._retrying = false;
      }
    },
    // For crawled sources (web/search) show whether we have the full page or
    // only a SERP snippet (full download blocked). Absent for tg/x/dzen.
    extractionBadge(msg) {
      const m = msg.extraction_method;
      if (!m || m === 'none') return null;
      if (m === 'snippet') {
        return {
          label: 'Сниппет', color: 'warning', icon: 'mdi-text-short',
          tooltip: 'Полная страница не скачалась (сайт заблокировал доступ) — показан фрагмент из поисковой выдачи',
        };
      }
      return {
        label: 'Полный текст', color: 'success', icon: 'mdi-text-long',
        tooltip: 'Скачан полный текст страницы',
      };
    },
    renderContent(msg) {
      const full = this.msgContent(msg);
      let raw = msg._expanded ? full : this.truncate(full, 300);
      // не резать маркер парсера {{LINK:…}} / {{BLOCKQUOTE:…}} посередине при усечении
      const cutAt = raw.lastIndexOf('{{');
      if (cutAt >= 0 && raw.indexOf('}}', cutAt) < 0) raw = raw.slice(0, cutAt).trimEnd() + '…';
      let html = this.escapeHtml(raw);

      // Маркеры парсера Telegram (тот же формат, что в Messages.vue):
      // {{LINK:url|text}} → <a>, {{BLOCKQUOTE:text}} → <blockquote>,
      // любой неизвестный {{XXX:text}} → просто text, чтобы в UI не утекала разметка.
      html = html.replace(/\{\{LINK:([^|]+)\|([^}]+)\}\}/g, (m, url, text) => {
        const cls = url.includes('t.me/') ? 'channel-link' : 'external-link';
        return `<a href="${url}" target="_blank" rel="noopener" class="${cls}">${text}</a>`;
      });
      html = html.replace(/\{\{BLOCKQUOTE:([\s\S]*?)\}\}/g, '<blockquote class="message-blockquote">$1</blockquote>');
      html = html.replace(/\{\{[A-Z_]+:([\s\S]*?)\}\}/g, '$1');
      html = dropLeftoverMarkers(html);

      // Linkify @mentions → x.com (Twitter) only; other platforms keep raw text.
      if (this.isTwitter(msg)) {
        html = html.replace(
          /(^|[^\w@])@([A-Za-z0-9_]{1,15})/g,
          (_, pre, handle) =>
            `${pre}<a href="https://x.com/${handle}" target="_blank" rel="noopener" class="mention-link">@${handle}</a>`,
        );
      }

      // Highlight search term (only set when the source is a search query).
      if (this.highlightTerm) {
        const re = new RegExp(this.escapeRegExp(this.highlightTerm), 'gi');
        html = html.replace(re, (m) => `<mark>${m}</mark>`);
      }
      return html;
    },
    close() {
      this.dialogVisible = false;
    },
  },
};
</script>

<style scoped>
.messages-container {
  height: 70vh;
  overflow-y: auto;
  padding: 1rem;
}
.message-item {
  border-bottom: 1px solid rgb(var(--v-theme-outline-variant));
  padding: 1rem 0;
}
.message-item:last-child {
  border-bottom: none;
}
.message-site {
  font-weight: 600;
  font-size: 0.8rem;
  color: rgb(var(--v-theme-primary));
  word-break: break-all;
  margin-bottom: 0.15rem;
}
.message-date {
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
  font-size: 0.9rem;
}
.message-date-note {
  font-size: 0.7rem;
  color: rgb(var(--v-theme-on-surface));
  opacity: 0.6;
}
.message-link {
  margin-top: 0.25rem;
}
.message-link a {
  text-decoration: none;
  font-size: 0.85rem;
}
.message-link a:hover {
  text-decoration: underline;
}
.article-title {
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-size: 1rem;
}
.main-text {
  color: rgb(var(--v-theme-on-surface));
}
.messages-container::-webkit-scrollbar {
  width: 8px;
}
.messages-container::-webkit-scrollbar-thumb {
  background: rgba(var(--v-theme-on-surface), 0.2);
  border-radius: 4px;
}
.author-header {
  display: flex;
  align-items: center;
}
.author-name {
  font-weight: 600;
  line-height: 1.1;
  color: rgb(var(--v-theme-on-surface));
  font-size: 0.95rem;
}
.author-handle {
  font-size: 0.8rem;
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
}
.author-handle:hover {
  text-decoration: underline;
}
.main-text :deep(.message-blockquote) {
  margin: 6px 0; padding: 4px 10px; border-left: 3px solid rgba(25,118,210,0.6);
  background: rgba(25,118,210,0.06); border-radius: 0 4px 4px 0; white-space: pre-wrap;
}
.main-text :deep(a.channel-link), .main-text :deep(a.external-link) { color: rgb(var(--v-theme-primary)); }
</style>

<!-- unscoped styles so v-html content (mentions, highlight) picks them up -->
<style>
.main-text .mention-link {
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
}
.main-text .mention-link:hover {
  text-decoration: underline;
}
.main-text mark {
  background-color: #fff59d;
  color: inherit;
  padding: 0 2px;
  border-radius: 2px;
}
</style>
